/**
 * ChatSidebar — conversation list panel.
 *
 * Shows: active conversations with unread counts, last message preview,
 * search filter, "New Conversation" button.
 * Pinned conversations sort to the top.
 * Used inside both the ChatTray and the full ChatPage.
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { ConversationResponse } from '../../api/chat';
import UserAvatar from '../UserAvatar/UserAvatar';
import styles from './Chat.module.css';

interface ChatSidebarProps {
  conversations: ConversationResponse[];
  activeConversationId?: string | null;
  currentUserGuid: string;
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
  onArchiveConversation?: (id: string) => void;
  onPinConversation?: (id: string) => void;
  loading?: boolean;
  /** Hide the "Messages" heading (used when parent already shows a header, e.g. ChatTray). */
  hideTitle?: boolean;
}

export default function ChatSidebar({
  conversations,
  activeConversationId,
  currentUserGuid,
  onSelectConversation,
  onNewConversation,
  onArchiveConversation,
  onPinConversation,
  loading = false,
  hideTitle = false,
}: ChatSidebarProps) {
  const { t } = useAppTranslation('chat');
  const [search, setSearch] = useState('');

  /** Display name for a conversation: group name, or other participant's name for DMs. */
  const getDisplayName = useCallback(
    (conv: ConversationResponse): string => {
      if (conv.name) return conv.name;
      // Direct message — show the other participant's name
      if (conv.conversationType === 'direct') {
        const other = conv.participants.find(
          (p) => p.userGuid.toUpperCase() !== currentUserGuid.toUpperCase()
        );
        return other?.displayName || other?.screenName || t('conversation.directMessage');
      }
      return conv.conversationType === 'project_team'
        ? t('conversation.projectTeam')
        : t('conversation.groupChat');
    },
    [currentUserGuid, t]
  );

  /** Sort: pinned first, then by last message date. */
  const sorted = useMemo(() => {
    return [...conversations].sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      // Within same pin status, sort by last message time (newest first)
      const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [conversations]);

  const filtered = useMemo(() => {
    if (!search.trim()) return sorted;
    const q = search.toLowerCase();
    return sorted.filter((c) => {
      const name = getDisplayName(c).toLowerCase();
      return name.includes(q);
    });
  }, [sorted, search, getDisplayName]);

  const formatLastTime = (dateStr: string | null): string => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const now = new Date();
      const diff = now.getTime() - d.getTime();
      if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      if (diff < 172800000) return t('conversation.yesterday');
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <div className={styles.chatSidebar}>
      {/* Header */}
      <div className={styles.sidebarHeader}>
        {!hideTitle && <h3 className={styles.sidebarTitle}>{t('nav.title')}</h3>}
        <button
          type="button"
          className={styles.newChatBtn}
          onClick={onNewConversation}
          title={t('nav.newChat')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            <line x1="12" y1="8" x2="12" y2="14" />
            <line x1="9" y1="11" x2="15" y2="11" />
          </svg>
        </button>
      </div>

      {/* Search */}
      <div className={styles.sidebarSearch}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder={t('nav.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Conversation list */}
      <div className={styles.conversationList}>
        {loading && (
          <div className={styles.loadingIndicator}>…</div>
        )}

        {!loading && filtered.length === 0 && (
          <div className={styles.emptyState}>{t('nav.noConversations')}</div>
        )}

        {filtered.map((conv) => {
          const isActive = conv.id === activeConversationId;
          const displayName = getDisplayName(conv);
          // Get avatar from other participant (DM) or first participant (group)
          const avatarParticipant = conv.conversationType === 'direct'
            ? conv.participants.find((p) => p.userGuid.toUpperCase() !== currentUserGuid.toUpperCase())
            : conv.participants[0];
          // DEV-1391 — groups can have a custom avatar; prefer it over derived.
          const groupAvatarUrl = conv.conversationType !== 'direct' ? (conv.avatarUrl || undefined) : undefined;

          return (
            <div
              key={conv.id}
              className={`${styles.conversationItem} ${isActive ? styles.conversationActive : ''}`}
            >
              <button
                type="button"
                className={styles.convClickArea}
                onClick={() => onSelectConversation(conv.id)}
              >
                {/* Avatar — custom group avatar if set, else derived (DEV-1391) */}
                {groupAvatarUrl ? (
                  <UserAvatar
                    userGuid={conv.id}
                    avatarUrl={groupAvatarUrl}
                    userName={displayName}
                    size="md"
                  />
                ) : avatarParticipant ? (
                  <UserAvatar
                    userGuid={avatarParticipant.userGuid}
                    avatarUrl={avatarParticipant.profileImageUrl || undefined}
                    userName={avatarParticipant.displayName || avatarParticipant.screenName || displayName}
                    size="md"
                    showPresence
                  />
                ) : null}

                {/* Info */}
                <div className={styles.convInfo}>
                  <div className={styles.convNameRow}>
                    {/* Pin indicator */}
                    {conv.isPinned && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="var(--accent-primary)" stroke="none" style={{ flexShrink: 0, marginInlineEnd: 4 }}>
                        <path d="M12 17l0 5M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
                      </svg>
                    )}
                    <span className={styles.convName}>{displayName}</span>
                    <span className={styles.convTime}>{formatLastTime(conv.lastMessageAt)}</span>
                  </div>
                  <div className={styles.convPreviewRow}>
                    <span className={styles.convPreview}>
                      {conv.lastMessagePreview || ''}
                    </span>
                    {conv.unreadCount > 0 && (
                      <span className={styles.unreadBadge}>{conv.unreadCount}</span>
                    )}
                  </div>
                </div>
              </button>

              {/* Hover actions: pin + archive */}
              <div className={styles.convHoverActions}>
                {onPinConversation && (
                  <button
                    type="button"
                    className={styles.convArchiveBtn}
                    title={conv.isPinned ? t('conversation.unpinConversation') : t('conversation.pinConversation')}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPinConversation(conv.id);
                    }}
                    style={{ color: conv.isPinned ? 'var(--accent-primary)' : undefined }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill={conv.isPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="17" x2="12" y2="22" /><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
                    </svg>
                  </button>
                )}
                {onArchiveConversation && (
                  <button
                    type="button"
                    className={styles.convArchiveBtn}
                    title={t('conversation.archive')}
                    onClick={(e) => {
                      e.stopPropagation();
                      onArchiveConversation(conv.id);
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
